'use client';
import { Fragment, useEffect } from 'react';

import { PlpMarketingTileItem } from 'src/data/Contentful/ContentfulListPlpMarketingTiles';
import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { IntersectionObserverHandler } from 'src/general/components/IntersectionObserverHandler/IntersectionObserverHandler';
import { pushCarmaRecommendationEvent } from 'src/general/helpers/pushCarmaRecommendationEvent';
import { getMarketingTile } from 'src/plp/helpers/getMarketingTile';
import { ProductListDataRow } from 'src/types/CataloguePage.types';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayerFeatureEngagement } from 'src/utils/pushToDataLayerFeatureEngagement';

import { ProductCard } from '../../../general/components/ProductCard/ProductCard';
import { ProductGrid } from '../ProductGrid/ProductGrid';
import { ProductGridSection } from '../ProductGridSection/ProductGridSection';
import { SearchAlertTileLowResults } from '../SearchAlertTile/SearchAlertTileLowResults';
import { SearchAlertToggle } from '../SearchAlertToggle/SearchAlertToggle';
import { SearchAlertToggleProvider } from '../SearchAlertToggle/SearchAlertToggleProvider';

type Props = {
  vehicle: ProductDetailsPayload;
  results: ProductListDataRow[];
  marketingTiles: PlpMarketingTileItem['tileContent'][];
  recommendationId: string | null;
};

const indexesMarketingTiles = [1, 7, 11];

const SimilarCarsPlp = ({ vehicle, results, marketingTiles, recommendationId }: Props) => {
  const originalCarTitle = `${vehicle.buildYear} ${vehicle.make} ${vehicle.model}`;
  const title = vehicle ? `Similar cars to ${originalCarTitle}` : 'Similar cars';

  useEffect(() => {
    pushToDataLayerFeatureEngagement({
      event: 'feature_engagement',
      payload: {
        type: 'load',
        feature: 'similarCars',
      },
    });
  }, []);

  return (
    <IntersectionObserverHandler
      margins="-50%"
      onIntersecting={() => {
        pushToDataLayerFeatureEngagement({
          event: 'feature_engagement',
          payload: {
            type: 'view',
            feature: 'similarCars',
          },
        });
      }}
    >
      <ProductGridSection
        title={title}
        hrefBack={`/product-detail/${vehicle.sku}`}
        action={
          <SearchAlertToggleProvider
            isSimilarTo={vehicle.sku}
            Component={SearchAlertToggle}
            placement="page-top"
            targetSlug={`product-detail/${vehicle.sku}/similar`}
          />
        }
      >
        <main className="flex flex-col gap-8 pb-8">
          <ProductGrid variant="watchlist">
            {results
              .filter((item) => item.sku !== vehicle.sku)
              .map((item, index) => (
                <Fragment key={item.sku}>
                  <li>
                    <ProductCard
                      variant="plp"
                      carData={productDetailPayloadToBaseCarData(item)}
                      weeklyRepayment={item.weeklyRepayment}
                      defaultRate={item.defaultRate}
                      imagePriority={index < 3}
                      onCardClick={() => {
                        pushToDataLayerFeatureEngagement({
                          event: 'feature_engagement_click',
                          payload: {
                            type: 'click',
                            feature: 'similarCars',
                            sku: item.sku,
                            index,
                          },
                        });
                        pushCarmaRecommendationEvent('similar', results, recommendationId)(item.sku);
                      }}
                    />
                  </li>
                  {getMarketingTile(indexesMarketingTiles, 0, index, marketingTiles)}
                </Fragment>
              ))}
          </ProductGrid>
          <SearchAlertToggleProvider
            isSimilarTo={vehicle.sku}
            Component={SearchAlertTileLowResults}
            placement="tile"
            targetSlug={`product-detail/${vehicle.sku}/similar`}
          />
        </main>
      </ProductGridSection>
    </IntersectionObserverHandler>
  );
};

export default SimilarCarsPlp;
